import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';
import {
	ContentWrap,
	Css,
	H1,
	H2,
	ITheme,
	Responsive,
} from '../../../../../ui';
import { Topbar } from '../../../../../components/common/top-bar/Topbar';
import { ISolicitationEntry } from '../../../../../contentful/types/ISolicitationEntry';
import SolicitationHeaderBackLink from './SolicitationHeaderBackLink';
import { Color } from '../../../../../styles/Color';
import SolicitationDetailsHeaderInfo from './SolicitationDetailsHeaderInfo';

interface ISolicitationDetailsHeader {
	siteSectionId: string;
	solicitation: ISolicitationEntry;
}

export default function SolicitationDetailsHeader({
	siteSectionId,
	solicitation,
}: ISolicitationDetailsHeader) {
	const isMobile = Responsive.useMatchesBreakpoint('mobileXL');
	const { title, status, location, solicitationNumber } = solicitation.fields;

	return (
		<>
			<Topbar siteSectionId={siteSectionId} />

			<SolicitationHeaderBackLink />

			<DetailsHeaderWrapper>
				<DetailsHeaderContent>
					<StyledContentWrap>
						<HeaderLeft>
							{status && isMobile && <Status>{status}</Status>}
							<HeaderTitleOverline>Project Name</HeaderTitleOverline>
							<Title styling="h4">{title}</Title>

							<SolicitationNumber styling="h5">
								{solicitationNumber}
							</SolicitationNumber>
						</HeaderLeft>
						<HeaderRight>
							{status && !isMobile && <Status>{status}</Status>}
							<LocationWrapper>
								<LocationOverline>Location(s)</LocationOverline>
								<Location>{location.join(', ')}</Location>
							</LocationWrapper>
						</HeaderRight>
					</StyledContentWrap>
				</DetailsHeaderContent>

				<SolicitationDetailsHeaderInfo solicitation={solicitation} />
			</DetailsHeaderWrapper>
		</>
	);
}

const DetailsHeaderWrapper = styled('div', {
	shouldForwardProp: isPropValid,
})<{}, ITheme>`
	color: ${(props) => props.theme.color.onPrimary};
`;

const DetailsHeaderContent = styled.div<{}, ITheme>`
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: flex-end;
	background-color: ${({ theme }) => theme.color.brand};
	color: ${({ theme }) => theme.color.onPrimary};
`;

const StyledContentWrap = styled(ContentWrap)`
	padding: ${Css.pxToRem(64)} 0;
	display: flex;
	width: 100%;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: auto;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		flex-direction: column;
		gap: ${Css.pxToRem(32)};
		padding: ${Css.pxToRem(24)} 0;
	}
`;

const HeaderLeft = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	width: 100%;
`;

const HeaderRight = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	justify-content: space-between;
	width: 100%;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		align-items: flex-start;
	}
`;

const HeaderTitleOverline = styled.div`
	font-weight: 500;
	font-size: ${Css.pxToRem(16)};
	margin-bottom: ${Css.pxToRem(16)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-bottom: 0;
		font-size: ${Css.pxToRem(12)};
	}
`;

const LocationOverline = styled(HeaderTitleOverline)`
	margin-bottom: ${Css.pxToRem(4)};
`;

const Title = styled(H1)<{}, ITheme>`
	color: ${({ theme }) => theme.color.onPrimary};
	margin-bottom: ${Css.pxToRem(16)};
	line-height: normal;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-bottom: ${Css.pxToRem(8)};
	}
`;

const SolicitationNumber = styled(H2)<{}, ITheme>`
	color: ${({ theme }) => theme.color.onPrimary};
`;

const Status = styled.div`
	font-weight: 500;
	font-size: ${Css.pxToRem(16)};
	line-height: ${Css.pxToRem(19)};
	width: fit-content;
	padding: ${Css.pxToRem(11)} ${Css.pxToRem(16)};
	background-color: ${Color.Neutral2};
	border-radius: 4px;
	height: fit-content;
	text-align: center;
	color: ${Color.OnBackground};
	text-transform: capitalize;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: ${Css.pxToRem(5)} ${Css.pxToRem(8)};
		margin-bottom: ${Css.pxToRem(32)};
	}
`;

const LocationWrapper = styled.div`
	text-align: end;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		text-align: start;
	}
`;

const Location = styled.div`
	font-weight: 500;
	font-size: ${Css.pxToRem(32)};
	margin-bottom: ${Css.pxToRem(8)};
	text-align: right;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		text-align: left;
		margin-bottom: 0;
	}
`;
